import {useState} from 'react'
import {useSelector} from 'react-redux'
import {authorizationHeaderFromAccessToken} from '../auth'
import {API_URL} from '../config'
import {captureException} from '../errors'
import ServerError from '../errors/server'
import {getAccessToken} from '../store/authentication/authentication.selectors'

type Options = Readonly<{
  onError?: (error: Error) => void
  onSuccess?: () => void
}>

function useUpdateSponsorName({onError, onSuccess}: Options = {}): {
  busy: boolean
  updateSponsorName: (sponsorId: string, name: string) => void
  error: Error | null
} {
  const accessToken = useSelector(getAccessToken)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const updateSponsorName = (sponsorId: string, name: string) => {
    setBusy(true)
    setError(null)

    fireUpdateSponsorName(accessToken, sponsorId, name)
      .then(() => onSuccess?.())
      .catch(error => {
        captureException(error)
        setError(error)
        onError?.(error)
      })
      .finally(() => setBusy(false))
  }

  return {busy, updateSponsorName, error}
}

async function fireUpdateSponsorName(
  accessToken: string,
  sponsorId: string,
  name: string
): Promise<void> {
  const url = new URL(`sponsors/${sponsorId}`, API_URL)

  const response = await fetch(url.toString(), {
    method: 'PUT',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...authorizationHeaderFromAccessToken(accessToken)
    },
    body: JSON.stringify({name})
  })

  if (response.status !== 204) {
    throw await ServerError.fromResponse(response)
  }
}

export default useUpdateSponsorName
